import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";
import { ITrayOrder, normalizeTrayCodes } from "common/interface/IMarkHoldOrder";
import { logoutIfNeeded } from "common/utils";

export interface MarkHoldOrdersState {
  trayOrder?: ITrayOrder | null;
  dispositions: string[];
  trayStatus: "idle" | "loading" | "success" | "error";
  trayError?: string;
  dispositionsStatus: "idle" | "loading" | "success" | "error";
  markFailedStatus: "idle" | "saving" | "success" | "error";
  markFailedError?: string;
}

const initialState: MarkHoldOrdersState = {
  trayOrder: undefined,
  dispositions: [],
  trayStatus: "idle",
  dispositionsStatus: "idle",
  markFailedStatus: "idle",
};

export const getOrderByTrayCode = createAsyncThunk<
  ITrayOrder | null,
  { trayCode: string; storeId: string }
>(
  "markHoldOrders/getOrderByTrayCode",
  async ({ trayCode, storeId }, thunkApi) => {
    try {
      const response = await API.get(`${apis.getOrderByTrayCode}/${trayCode}`, {
        headers: { storeid: storeId },
      });
      const order = response.data as ITrayOrder;
      if (order?.metadata) {
        order.metadata.trayCodes = normalizeTrayCodes(order.metadata);
      }
      return order;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      const errorMessage = error?.response?.data?.message || "Failed to fetch order";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getDeliveryFailureDispositions = createAsyncThunk<string[]>(
  "markHoldOrders/getDeliveryFailureDispositions",
  async (_, thunkApi) => {
    try {
      const response = await API.get(apis.getDeliveryFailureDispositions);
      return response.data as string[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to fetch reasons";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const markOrderFailed = createAsyncThunk<{}, { orderId: string; remarks: string }>(
  "markHoldOrders/markOrderFailed",
  async ({ orderId, remarks }, thunkApi) => {
    try {
      const response = await API.post(`${apis.markOrderFailed}/${orderId}/failed`, { remarks }, 
        {params: { markHoldFromWmsWeb: true }});
          // 200 response with a message means the action was blocked (e.g. geofence check failed)
      if (response.data?.message) {
        return thunkApi.rejectWithValue({ message: response.data.message });
      }
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to mark order as failed";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

const markHoldOrdersSlice = createSlice({
  name: "markHoldOrders",
  initialState,
  reducers: {
    resetTrayOrder: (state) => {
      state.trayOrder = undefined;
      state.trayStatus = "idle";
      state.trayError = undefined;
    },
    resetMarkFailedStatus: (state) => {
      state.markFailedStatus = "idle";
      state.markFailedError = undefined;
    },
  },
  extraReducers(builder) {
    builder
      /**
       * GET ORDER BY TRAY CODE
       */
      .addCase(getOrderByTrayCode.pending, (state) => {
        state.trayStatus = "loading";
        state.trayOrder = undefined;
        state.trayError = undefined;
      })
      .addCase(getOrderByTrayCode.fulfilled, (state, action) => {
        state.trayStatus = "success";
        state.trayOrder = action.payload;
      })
      .addCase(getOrderByTrayCode.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.trayStatus = "error";
        state.trayError = error?.message || "Failed to fetch order";
      })

      /**
       * GET DELIVERY FAILURE DISPOSITIONS
       */
      .addCase(getDeliveryFailureDispositions.pending, (state) => {
        state.dispositionsStatus = "loading";
      })
      .addCase(getDeliveryFailureDispositions.fulfilled, (state, action) => {
        state.dispositionsStatus = "success";
        state.dispositions = action.payload;
      })
      .addCase(getDeliveryFailureDispositions.rejected, (state) => {
        state.dispositionsStatus = "error";
        state.dispositions = [];
      })

      /**
       * MARK ORDER FAILED
       */
      .addCase(markOrderFailed.pending, (state) => {
        state.markFailedStatus = "saving";
        state.markFailedError = undefined;
      })
      .addCase(markOrderFailed.fulfilled, (state) => {
        state.markFailedStatus = "success";
      })
      .addCase(markOrderFailed.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.markFailedStatus = "error";
        state.markFailedError = error?.message || "Failed to mark order as failed";
      });
  },
});

export const { resetTrayOrder, resetMarkFailedStatus } = markHoldOrdersSlice.actions;
export default markHoldOrdersSlice.reducer;
